class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

      // Check if this is the last item in the cart and if we have a gift as well, remove them.
      const allItemsExceptGifts = cartItems.querySelectorAll(
        '.cart-item:not([data-lisa-gift="true"]):not([data-lisa-addon="true"])',
      );

      const removeObject = [];
      removeObject.push({
        key: this.dataset.key,
        index: this.dataset.index,
        quantity: 0,
      });

      // Is last non-gift item, remove gifts as well
      if (allItemsExceptGifts.length === 1) {
        const giftsOnly = cartItems.querySelectorAll('.cart-item[data-lisa-gift="true"]');
        giftsOnly.forEach((giftItem) => {
          removeObject.push({
            key: giftItem.dataset.key,
            index: giftItem.dataset.index,
            quantity: 0,
          });
        });
      }

      cartItems.updateQuantity(removeObject);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();

      if (event.source === 'discount-code-field' || event.source === 'component-discounts-remove' || event.source === 'gwp') {
        this.renderAdditionalSections();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const key = event.target.dataset.key;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity([
        {
          key: key,
          index: index,
          quantity: inputValue,
          name: event.target.getAttribute('name'),
          variantId: event.target.dataset.quantityVariantId,
        },
      ]);
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const isEmpty = html.querySelector('cart-drawer').classList.contains('is-empty');
          this.emptyCartToggle(isEmpty);

          const selectors = [
            'cart-drawer-items', 
            '.cart-drawer__footer', 
            '.cart-recommendations'
          ];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  emptyCartToggle(isEmpty) {
    this.classList.toggle('is-empty', isEmpty);

    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
  }

  /**
   * lineKey, lineIndex, quantity, name, variantId
   */
  updateQuantity(lines) {
    // Enable loading for all affected lines
    lines.forEach((l) => this.enableLoading(l.index));

    // Build updates object for Shopify's /cart/update.js endpoint
    const updates = {};
    lines.forEach((l) => {
      updates[l.key] = l.quantity;
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const firstIndex = lines[0].index;
        const firstQuantity = lines[0].quantity;
        const firstVariantId = lines[0].variantId;
        const firstName = lines[0].name;

        if (parsedState.errors) {
          // For single updates, restore the quantity input value
          const quantityElement =
            document.getElementById(`Quantity-${firstIndex}`) ||
            document.getElementById(`Drawer-quantity-${firstIndex}`);
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(firstIndex, parsedState.errors);
          return;
        }

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        this.emptyCartToggle(parsedState.item_count === 0);

        this.renderAdditionalSections(parsedState.sections);

        // Handle validation messages for single updates
        const quantityElement =
          document.getElementById(`Quantity-${firstIndex}`) || document.getElementById(`Drawer-quantity-${firstIndex}`);
        const items = document.querySelectorAll('.cart-item');
        const updatedValue = parsedState.items[firstIndex - 1] ? parsedState.items[firstIndex - 1].quantity : undefined;

        let message = '';
        if (
          items.length === parsedState.items.length &&
          quantityElement &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(firstIndex, message);

        // Handle focus management for single updates
        const lineItem =
          document.getElementById(`CartItem-${firstIndex}`) || document.getElementById(`CartDrawer-Item-${firstIndex}`);
        if (lineItem && lineItem.querySelector(`[name="${firstName}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${firstName}"]`))
            : lineItem.querySelector(`[name="${firstName}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: firstVariantId,
          action: firstQuantity === 0 ? 'remove' : 'update',
        });
      })
      .catch(() => {
        console.error('Error updating cart');
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        lines.forEach((l) => this.disableLoading(l.index));
      });
  }

  renderAdditionalSections(sections) {
    if (sections) {
      this.replaceInnerHTMLFromSections(sections);

      return;
    }

    const allSectionIds = this.getSectionsToRender()
      .map((section) => section.section)
      .join(',');

    fetch(`${routes.cart_url}?sections=${allSectionIds}`)
      .then((response) => response.json())
      .then((responseText) => {
        this.replaceInnerHTMLFromSections(responseText);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  replaceInnerHTMLFromSections(parsedStateSection) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      if (!elementToReplace) return;

      const newSectionHTML = parsedStateSection[section.section] || parsedStateSection[section.id];

      const newInnerHTML = this.getSectionInnerHTML(newSectionHTML, section.selector);
      if (!newInnerHTML) return;

      elementToReplace.innerHTML = newInnerHTML;
    });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.cartNoteMaxLengthElement = this.querySelector('[data-cart-note-max-length]');
        this.maxNoteLength = this.cartNoteMaxLengthElement
          ? parseInt(this.cartNoteMaxLengthElement.dataset.cartNoteMaxLength)
          : null;

        this.addEventListener('input', (event) => {
          const value = event.target.value;

          if (this.maxNoteLength) {
            if (value.length > this.maxNoteLength) {
              event.target.value = value.slice(0, this.maxNoteLength);
              return;
            }

            this.cartNoteMaxLengthElement.textContent = event.target.value.length;
          }

          debounce(() => {
            const body = JSON.stringify({ note: value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)();
        });
      }
    },
  );
}
